import React from "react";
import { createRoot } from "react-dom/client";
import "./index.scss";
import "mapbox-gl/dist/mapbox-gl.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "@dr.pogodin/react-helmet";
import { DialogContextProvider } from "@medoma/components/Dialog/DialogContextProvider";
import { isClientSideError } from "./Utils/ErrorUtils";
import { userManager, ENVIRONMENT } from "./Utils/EnvUtils";
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigationType,
} from "react-router";
import { Login } from "./pages/login/IdsLogin";
import RoutesAndActivities from "./pages/ambulating/RoutesAndActivities/RoutesAndActivities";
import VisitDetails from "./pages/ambulating/RoutesAndActivities/$routeId/visits/$visitId";
import AmbulatingContainer from "./pages/ambulating/AmbulatingContainer";
import {
  Patient,
  PatientNavigation,
} from "./pages/ambulating/patients/$patientId/Patient";
import { Patients } from "./pages/ambulating/patients/Patients";
import { RegisterPatient } from "./pages/ambulating/patients/register/RegisterPatient";
import { PatientIdentifierCheck } from "./pages/ambulating/patients/register/PatientIdentifierCheck";
import { RegisterNewPatientFormContainer } from "./forms/RegisterPatientForm/RegisterNewPatientForm";
import { RegisterReturningPatientFormContainer } from "./forms/RegisterPatientForm/RegisterReturningPatientForm";
import { Information } from "./pages/ambulating/patients/$patientId/information/Information";
import { InformationList } from "./pages/ambulating/patients/$patientId/information/InformationList";
import {
  EditAddressWrapper,
  EditAdditionalAddressInformationWrapper,
  EditKeyToPatientWrapper,
  EditExternalCareEpisodeIdWrapper,
  EditDiagnosisCodeWrapper,
  EditEconomicUnitWrapper,
  EditFlagsWrapper,
  EditHealthcareJourneyNumberWrapper,
  EditHomeCareWrapper,
  EditLifeCareWrapper,
  EditNameWrapper,
  EditOtherInformationWrapper,
  EditPetsWrapper,
  EditPhoneNumberWrapper,
  EditPlannedAdmissionDateTimeWrapper,
  EditPlannedDischargeDateTimeWrapper,
  EditPrimaryResponsibleWrapper,
  EditRelativesWrapper,
  EditSafetyAlarmWrapper,
  EditReferringUnitWrapper,
  EditAreaWrapper,
  EditTeamWrapper,
  EditWardWrapper,
  EditPlaceOfAssessmentWrapper,
  EditCareSummaryWrapper,
  EditPaymentExemptWrapper,
  EditLanguageWrapper,
  EditReasonForAdmissionWrapper,
} from "./pages/ambulating/patients/$patientId/information/EditInformationWrappers";
import { AuthProvider } from "react-oidc-context";
import { TabsView as GoTabsView } from "./pages/ambulating";
import Pushy from "pushy-sdk-web";
import * as Sentry from "@sentry/react";
import { I18nApp } from "./I18nApp";
import { RequireGoAuth } from "./components/RequireAuth/RequireGoAuth";
import { PatientAuthentication } from "./pages/ambulating/patients/$patientId/authentication/PatientAuthentication";
import { AdmitParentRoutesPatient } from "./pages/ambulating/patients/$patientId/admit/AdmitPatient";
import { CareEpisodeParentRoutesPatient } from "./pages/ambulating/patients/$patientId/careEpisode/CareEpisode";
import { DischargeParentRoutesPatient } from "./pages/ambulating/patients/$patientId/careEpisode/discharge/DischargePatient";
import { AdmitPatientInVisit } from "./pages/ambulating/RoutesAndActivities/$routeId/visits/$visitId/admit/AdmitPatientInVisit";
import { DeclineParentRoutesPatient } from "./pages/ambulating/patients/$patientId/decline/DeclinePatient";
import { DeclinePatientInVisit } from "./pages/ambulating/RoutesAndActivities/$routeId/visits/$visitId/decline/DeclinePatientInVisit";
import { Trans } from "@lingui/react/macro";
import { Activities } from "./pages/ambulating/patients/$patientId/activities/Activities";
import { ActivitiesPage } from "./pages/ambulating/patients/$patientId/activities/ActivitiesPage";
import { ActivityOccurrenceDetails } from "./pages/ambulating/patients/$patientId/activities/details/ActivityOccurrenceDetails";
import { ActivityOccurrenceEdit } from "./pages/ambulating/patients/$patientId/activities/details/ActivityOccurrenceEdit";
import {
  EditActivityTitleWrapper,
  EditActivityDescriptionWrapper,
  EditActivityHiddenWrapper,
  EditActivityDoubleStaffingWrapper,
  EditActivityDurationWrapper,
  EditActivityRequiredCompetencesWrapper,
  EditActivityMeasurementsWrapper,
  EditActivityEndDateWrapper,
  EditActivityOccurrenceNoteWrapper,
  EditActivityOccurrenceStartTimeWrapper,
  EditActivityScheduleWrapper,
} from "./pages/ambulating/patients/$patientId/activities/details/EditActivityWrappers";
import { GroupDetails } from "./pages/ambulating/patients/$patientId/activities/details/GroupDetails";
import { Coworkers } from "./pages/ambulating/coworkers/Coworkers";
import { Chat } from "./pages/ambulating/chat/Chat";
import { ChatWithCenter } from "./pages/ambulating/chat/ChatWithCenter";

Sentry.init({
  debug: import.meta.env.VITE_SENTRY,
  enabled: import.meta.env.VITE_SENTRY || import.meta.env.PROD,
  dsn: "https://463f26cd8272ee13cad9b114c611b356@o4511035982479360.ingest.de.sentry.io/4511150469414992",
  environment: ENVIRONMENT,
  // Sentry's equivalent of the v10 defaults: v11 collects more by default, and
  // we have no need for more. User data is only what Sentry.setUser is given,
  // not, for example, the IP address. Opt in per key when a need arises.
  dataCollection: {
    userInfo: false,
    cookies: false,
    httpHeaders: {
      request: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
      response: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
    },
    httpBodies: [],
    urlQueryParams: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
    genAI: { inputs: false, outputs: false },
    databaseQueryData: false,
    graphQL: { document: false, variables: false },
  },
  integrations: [
    Sentry.reactRouterBrowserTracingIntegration({
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],
  // go and center share the org's 5M spans/month quota. In Oct 2026, release
  // health counted ~85K sessions per day when a session still started on
  // every page load and navigation, which is also when a trace starts.
  // Assuming each app sees that many traces and ~100 spans per trace, 1%
  // fills the quota. After a few days, compare actual span usage in Sentry
  // Stats with the quota and adjust.
  tracesSampleRate: 0.01,
  // Session Replay
  replaysSessionSampleRate: 0, // This sets the sample rate at 0%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

if (import.meta.env.DEV && import.meta.env.VITE_MSW) {
  const { worker, HttpNetworkFrame } = await import("./mocks/browser");
  await worker.start({
    onUnhandledFrame({ frame }) {
      if (
        frame instanceof HttpNetworkFrame &&
        frame.data.request.url.includes("medoma")
      ) {
        console.error(
          "Found an unhandled %s request to %s",
          frame.data.request.method,
          frame.data.request.url,
        );
      }
    },
  });
  worker.listHandlers();
}

// Force stop background token refresh when Go is backgrounded
window.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") {
    userManager.stopSilentRenew();
  } else {
    // Restart background token refresh when Go is foregrounded
    userManager.startSilentRenew();
    // Check for expired or missing token
    userManager
      .getUser()
      .then((user) => {
        if (!user || user.expired) {
          // Pre-emptively trigger an MFA check rather than waiting for a failed request
          void userManager.signinRedirect({
            url_state: user?.profile.sub,
            state: window.location.pathname,
            acr_values: "mfa",
          });
        }
      })
      .catch(() => {
        void userManager.signinRedirect({
          state: window.location.pathname,
          acr_values: "mfa",
        });
      });
  }
});

// Navigate on push notification click
Pushy.setNotificationListener((data) => {
  if (!JSON.stringify(data).includes("navigate")) {
    return;
  }
  if (!JSON.stringify(data).includes("_pushy")) {
    return;
  }
  if (data?.action === "navigate" && data.url) {
    void router.navigate(new URL(data.url).pathname);
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, error) => {
        return count < 3 && !isClientSideError(error);
      },
    },
  },
});

const router = Sentry.wrapCreateBrowserRouter(createBrowserRouter)([
  {
    path: "/",
    element: (
      <RequireGoAuth>
        <AmbulatingContainer />
      </RequireGoAuth>
    ),
    errorElement: (
      <div>
        <Trans>
          <h1>Något gick fel</h1>
          <p>Ladda om sidan</p>
        </Trans>
      </div>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="routesAndActivities" replace />,
      },
      {
        element: <GoTabsView />,
        children: [
          {
            path: "routesAndActivities",
            children: [
              {
                index: true,
                element: <RoutesAndActivities />,
              },
              {
                path: ":routeId/visits/:visitId",
                children: [
                  {
                    index: true,
                    element: <VisitDetails />,
                  },
                  {
                    path: "admit",
                    element: <AdmitPatientInVisit />,
                  },
                  {
                    path: "decline",
                    element: <DeclinePatientInVisit />,
                  },
                ],
              },
            ],
          },
          {
            path: "patients",
            children: [
              {
                index: true,
                element: <Patients />,
              },
              {
                path: "register",
                element: <RegisterPatient />,
                children: [
                  {
                    index: true,
                    element: <PatientIdentifierCheck />,
                  },
                  {
                    path: "new-prospect",
                    element: <RegisterNewPatientFormContainer />,
                  },
                  {
                    path: "returning",
                    element: <RegisterReturningPatientFormContainer />,
                  },
                ],
              },
              {
                path: ":patientId",
                element: <Patient />,
                children: [
                  {
                    index: true,
                    element: <PatientNavigation />,
                  },
                  {
                    path: "admit",
                    element: <AdmitParentRoutesPatient />,
                  },
                  {
                    path: "care-episode",
                    element: <CareEpisodeParentRoutesPatient />,
                  },
                  {
                    path: "care-episode/discharge",
                    element: <DischargeParentRoutesPatient />,
                  },
                  {
                    path: "decline",
                    element: <DeclineParentRoutesPatient />,
                  },
                  {
                    path: "activities",
                    element: <Activities />,
                    children: [
                      {
                        index: true,
                        element: <ActivitiesPage />,
                      },
                      {
                        path: ":activityId/occurrences/:occurrenceId",
                        element: <Outlet />,
                        children: [
                          {
                            index: true,
                            element: <ActivityOccurrenceDetails />,
                          },
                          {
                            path: "edit",
                            element: <ActivityOccurrenceEdit />,
                          },
                          {
                            path: "edit/edit-title",
                            element: <EditActivityTitleWrapper />,
                          },
                          {
                            path: "edit/edit-description",
                            element: <EditActivityDescriptionWrapper />,
                          },
                          {
                            path: "edit/edit-hidden",
                            element: <EditActivityHiddenWrapper />,
                          },
                          {
                            path: "edit/edit-double-staffing",
                            element: <EditActivityDoubleStaffingWrapper />,
                          },
                          {
                            path: "edit/edit-duration",
                            element: <EditActivityDurationWrapper />,
                          },
                          {
                            path: "edit/edit-required-competences",
                            element: <EditActivityRequiredCompetencesWrapper />,
                          },
                          {
                            path: "edit/edit-measurements",
                            element: <EditActivityMeasurementsWrapper />,
                          },
                          {
                            path: "edit/edit-end-date",
                            element: <EditActivityEndDateWrapper />,
                          },
                          {
                            path: "edit/edit-note",
                            element: <EditActivityOccurrenceNoteWrapper />,
                          },
                          {
                            path: "edit/edit-start-time",
                            element: <EditActivityOccurrenceStartTimeWrapper />,
                          },
                          {
                            path: "edit/edit-schedule",
                            element: <EditActivityScheduleWrapper />,
                          },
                        ],
                      },
                      {
                        path: "groups/:groupId",
                        element: <GroupDetails />,
                      },
                    ],
                  },
                  {
                    path: "information",
                    element: <Information />,
                    children: [
                      {
                        index: true,
                        element: <InformationList />,
                      },
                      {
                        path: "edit-diagnosis-code",
                        element: <EditDiagnosisCodeWrapper />,
                      },
                      {
                        path: "edit-external-care-episode-id",
                        element: <EditExternalCareEpisodeIdWrapper />,
                      },
                      {
                        path: "edit-planned-admission-date-time",
                        element: <EditPlannedAdmissionDateTimeWrapper />,
                      },
                      {
                        path: "edit-planned-discharge-date-time",
                        element: <EditPlannedDischargeDateTimeWrapper />,
                      },
                      {
                        path: "edit-address",
                        element: <EditAddressWrapper />,
                      },
                      {
                        path: "edit-additional-address-information",
                        element: <EditAdditionalAddressInformationWrapper />,
                      },
                      {
                        path: "edit-key-to-patient",
                        element: <EditKeyToPatientWrapper />,
                      },
                      {
                        path: "edit-name",
                        element: <EditNameWrapper />,
                      },
                      {
                        path: "edit-phone-number",
                        element: <EditPhoneNumberWrapper />,
                      },
                      {
                        path: "edit-relatives",
                        element: <EditRelativesWrapper />,
                      },
                      {
                        path: "edit-primary-responsible",
                        element: <EditPrimaryResponsibleWrapper />,
                      },
                      {
                        path: "edit-other-information",
                        element: <EditOtherInformationWrapper />,
                      },
                      {
                        path: "edit-safety-alarm",
                        element: <EditSafetyAlarmWrapper />,
                      },
                      {
                        path: "edit-pets",
                        element: <EditPetsWrapper />,
                      },
                      {
                        path: "edit-flags",
                        element: <EditFlagsWrapper />,
                      },
                      {
                        path: "edit-care-summary",
                        element: <EditCareSummaryWrapper />,
                      },
                      {
                        path: "edit-healthcare-journey-number",
                        element: <EditHealthcareJourneyNumberWrapper />,
                      },
                      {
                        path: "edit-homecare",
                        element: <EditHomeCareWrapper />,
                      },
                      {
                        path: "edit-lifecare",
                        element: <EditLifeCareWrapper />,
                      },
                      {
                        path: "edit-referring-unit",
                        element: <EditReferringUnitWrapper />,
                      },
                      {
                        path: "edit-area",
                        element: <EditAreaWrapper />,
                      },
                      {
                        path: "edit-team",
                        element: <EditTeamWrapper />,
                      },
                      {
                        path: "edit-economic-unit",
                        element: <EditEconomicUnitWrapper />,
                      },
                      {
                        path: "edit-ward",
                        element: <EditWardWrapper />,
                      },
                      {
                        path: "edit-place-of-assessment",
                        element: <EditPlaceOfAssessmentWrapper />,
                      },
                      {
                        path: "edit-payment-exempt",
                        element: <EditPaymentExemptWrapper />,
                      },
                      {
                        path: "edit-language",
                        element: <EditLanguageWrapper />,
                      },
                      {
                        path: "edit-reason-for-admission",
                        element: <EditReasonForAdmissionWrapper />,
                      },
                    ],
                  },
                  {
                    path: "authentication",
                    element: <PatientAuthentication />,
                  },
                ],
              },
            ],
          },
          {
            path: "coworkers",
            element: <Coworkers />,
          },
        ],
      },
      {
        path: "chat",
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <Chat />,
          },
          {
            path: ":site/center",
            element: <ChatWithCenter />,
          },
        ],
      },
    ],
  },
  {
    path: "login",
    element: <Login />,
  },
  {
    path: "*",
    element: <Navigate to="/" />,
  },
  {
    path: "status",
    element: <p>OK</p>,
  },
]);

// https://github.com/authts/react-oidc-context/blob/f175dcba6ab09871b027d6a2f2224a17712b67c5/src/AuthProvider.tsx#L20-L30
const removeCodeAndStateParametersFromURL = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

const root = createRoot(document.getElementById("root")!);
root.render(
  <React.StrictMode>
    <AuthProvider
      userManager={userManager}
      onSigninCallback={removeCodeAndStateParametersFromURL}
    >
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <I18nApp>
            <DialogContextProvider>
              <RouterProvider router={router} />
            </DialogContextProvider>
          </I18nApp>
        </HelmetProvider>
      </QueryClientProvider>
    </AuthProvider>
  </React.StrictMode>,
);
